import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, TruckIcon, PackageIcon, Sparkles, CheckCircle2, TruckIcon as TruckEmpty, PackageCheck, Settings, XCircle, WifiOff, Info, ScrollText } from 'lucide-react';
import { AlertFeed } from './AlertFeed';
import { DriverList } from './DriverList';
import { DeliveryList } from './DeliveryList';
import { TriagePanel } from './TriagePanel';
import { LogsPanel, LogEntry } from './LogsPanel';
import { Alert, Driver, Delivery, TriageItem, Severity, mockDrivers } from '../data/mockData';
type PanelView = 'alerts' | 'fleet' | 'deliveries' | 'triage' | 'logs' | null;
interface LeftPanelProps {
  alerts: Alert[];
  drivers: Driver[];
  deliveries: Delivery[];
  triageItems: TriageItem[];
  selectedAlertId: string | null;
  selectedDriverId: string | null;
  onSelectAlert: (alertId: string, driverId: string) => void;
  onSelectDriver: (driverId: string) => void;
  onSelectDeliveryDriver: (driverId: string, deliveryRef?: string) => void;
  requestedAlertFilter?: {
    severity: 'all' | 'critical' | 'warning' | 'low';
    ts: number;
  } | null;
  onContactDriver?: (driverId: string) => void;
  onHoverAlert?: (driverIds: string[] | null) => void;
  resolvedAlertIds?: string[];
  dismissedAlertIds?: string[];
  onResolveAlert?: (alertId: string) => void;
  onDismissAlert?: (alertId: string) => void;
  logEntries?: LogEntry[];
}
export function LeftPanel({
  alerts,
  drivers,
  deliveries,
  triageItems,
  selectedAlertId,
  selectedDriverId,
  onSelectAlert,
  onSelectDriver,
  onSelectDeliveryDriver,
  requestedAlertFilter,
  onContactDriver,
  onHoverAlert,
  resolvedAlertIds = [],
  dismissedAlertIds = [],
  onResolveAlert,
  onDismissAlert,
  logEntries = []
}: LeftPanelProps) {
  const [activeView, setActiveView] = useState<PanelView>(null);
  const [alertTab, setAlertTab] = useState<'active' | 'resolved' | 'dismissed'>('active');
  const [fleetTab, setFleetTab] = useState<'active' | 'inactive'>('active');
  const [deliveryTab, setDeliveryTab] = useState<'enRoute' | 'delivered' | 'undelivered'>('enRoute');
  const criticalCount = alerts.filter(a => a.severity === 'critical' || a.severity === 'warning').length;
  useEffect(() => {
    if (requestedAlertFilter) {
      setActiveView('alerts');
    }
  }, [requestedAlertFilter]);
  const handleToggle = (view: PanelView) => {
    setActiveView(prev => prev === view ? null : view);
  };
  const panelHeaders: Record<string, {
    title: string;
    subtitle: string;
  }> = {
    alerts: {
      title: 'Active Alerts',
      subtitle: `${alerts.length} alerts · ${criticalCount} need attention`
    },
    fleet: {
      title: 'Fleet',
      subtitle: `${drivers.length} drivers · ${drivers.reduce((sum, d) => sum + d.cargo.length, 0)} active shipments`
    },
    deliveries: {
      title: 'Deliveries',
      subtitle: `${deliveries.length} total · ${deliveries.filter(d => d.status === 'at-risk').length} at risk`
    },
    triage: {
      title: 'Triage',
      subtitle: "Proactive recommendations · You're always in control"
    },
    logs: {
      title: 'Activity Log',
      subtitle: `${logEntries.length} event${logEntries.length !== 1 ? 's' : ''} logged`
    }
  };
  const activeDrivers = drivers.filter(d => d.activityStatus !== 'sleeper');
  const inactiveDrivers = drivers.filter(d => d.activityStatus === 'sleeper');
  const enRouteDeliveries = deliveries.filter(d => d.status === 'pending' || d.status === 'at-risk');
  const deliveredDeliveries = deliveries.filter(d => d.status === 'completed');
  const undeliveredDeliveries: Delivery[] = [];
  return <div className="flex h-full shrink-0 z-10" data-id="element-433">
      {/* Icon Rail */}
      <div className="w-[72px] h-full bg-slate-950/95 border-r border-slate-800 flex flex-col items-center pt-4 shrink-0" data-id="element-434">
        <div className="flex flex-col items-center gap-2" data-id="element-435">
          <RailButton icon={<AlertTriangle className="w-5 h-5" data-id="element-437" />} label="Alerts" isActive={activeView === 'alerts'} onClick={() => handleToggle('alerts')} hasUnread={criticalCount > 0} data-id="element-436" />
          <RailButton icon={<TruckIcon className="w-5 h-5" data-id="element-439" />} label="Fleet" isActive={activeView === 'fleet'} onClick={() => handleToggle('fleet')} data-id="element-438" />
          <RailButton icon={<PackageIcon className="w-5 h-5" data-id="element-441" />} label="Deliveries" isActive={activeView === 'deliveries'} onClick={() => handleToggle('deliveries')} data-id="element-440" />
          <RailButton icon={<Sparkles className="w-5 h-5" data-id="element-443" />} label="Triage" isActive={activeView === 'triage'} onClick={() => handleToggle('triage')} data-id="element-442" />
          <RailButton icon={<ScrollText className="w-5 h-5" data-id="element-445" />} label="Logs" isActive={activeView === 'logs'} onClick={() => handleToggle('logs')} hasUnread={logEntries.length > 0 && activeView !== 'logs'} data-id="element-444" />
        </div>
        <div className="mt-auto pb-4 flex flex-col items-center gap-3" data-id="element-446">
          <button className="relative flex items-center justify-center w-9 h-9 rounded-full bg-brand-blue/20 border border-brand-blue/30 hover:bg-brand-blue/30 transition-all" data-id="element-447">
            <span className="text-xs font-bold text-brand-blue" data-id="element-448">TD</span>
          </button>
          <button className="flex items-center justify-center w-10 h-10 rounded-xl text-slate-500 hover:text-slate-300 hover:bg-slate-900 transition-all" data-id="element-449">
            <Settings className="w-5 h-5" data-id="element-450" />
          </button>
        </div>
      </div>

      {/* Expandable Panel */}
      <AnimatePresence data-id="element-451">
        {activeView && <motion.div initial={{
        width: 0,
        opacity: 0
      }} animate={{
        width: 400,
        opacity: 1
      }} exit={{
        width: 0,
        opacity: 0
      }} transition={{
        type: 'spring',
        damping: 28,
        stiffness: 320
      }} className="h-full overflow-hidden border-r border-slate-800 bg-slate-950/90 backdrop-blur-md flex flex-col" data-id="element-452">
            <div className="w-[400px] h-full flex flex-col" data-id="element-453">
              {/* Panel Header */}
              <div className="shrink-0 border-b border-slate-800" data-id="element-454">
                {activeView === 'alerts' ? <div className="flex px-4 pt-3" data-id="element-455">
                    {(['active', 'resolved', 'dismissed'] as const).map(tab => <button key={tab} onClick={() => setAlertTab(tab)} className={`relative px-4 pb-2.5 text-sm font-medium transition-colors ${alertTab === tab ? 'text-slate-100' : 'text-slate-500 hover:text-slate-300'}`} data-id="element-456">
                          {tab === 'active' ? 'Active' : tab === 'resolved' ? 'Resolved' : 'Dismissed'}
                          {alertTab === tab && <motion.div layoutId="alertTabIndicator" className="absolute bottom-0 left-0 right-0 h-[2px] bg-brand-blue rounded-full" transition={{
                  type: 'spring',
                  damping: 30,
                  stiffness: 400
                }} data-id="element-457" />}
                        </button>)}
                  </div> : activeView === 'fleet' ? <div className="flex px-4 pt-3" data-id="element-458">
                    {(['active', 'inactive'] as const).map(tab => <button key={tab} onClick={() => setFleetTab(tab)} className={`relative px-4 pb-2.5 text-sm font-medium transition-colors ${fleetTab === tab ? 'text-slate-100' : 'text-slate-500 hover:text-slate-300'}`} data-id="element-459">
                        {tab === 'active' ? 'Active' : 'Inactive'}
                        {fleetTab === tab && <motion.div layoutId="fleetTabIndicator" className="absolute bottom-0 left-0 right-0 h-[2px] bg-brand-blue rounded-full" transition={{
                  type: 'spring',
                  damping: 30,
                  stiffness: 400
                }} data-id="element-460" />}
                      </button>)}
                  </div> : activeView === 'deliveries' ? <div className="flex px-4 pt-3" data-id="element-461">
                    {(['enRoute', 'delivered', 'undelivered'] as const).map(tab => <button key={tab} onClick={() => setDeliveryTab(tab)} className={`relative px-4 pb-2.5 text-sm font-medium transition-colors ${deliveryTab === tab ? 'text-slate-100' : 'text-slate-500 hover:text-slate-300'}`} data-id="element-462">
                          {tab === 'enRoute' ? 'En Route' : tab === 'delivered' ? 'Delivered' : 'Undelivered'}
                          {deliveryTab === tab && <motion.div layoutId="deliveryTabIndicator" className="absolute bottom-0 left-0 right-0 h-[2px] bg-brand-blue rounded-full" transition={{
                  type: 'spring',
                  damping: 30,
                  stiffness: 400
                }} data-id="element-463" />}
                        </button>)}
                  </div> : <div className="p-4 pb-2" data-id="element-464">
                    <h2 className="text-sm font-semibold text-slate-100 flex items-center gap-2" data-id="element-465">
                      {activeView === 'triage' && <Sparkles className="w-4 h-4 text-brand-blue" data-id="element-466" />}
                      {activeView === 'logs' && <ScrollText className="w-4 h-4 text-brand-blue" data-id="element-467" />}
                      {panelHeaders[activeView].title}
                    </h2>
                  </div>}
              </div>

              {/* Panel Content */}
              {activeView === 'alerts' && alertTab === 'active' && <AlertFeed alerts={alerts} selectedAlertId={selectedAlertId} onSelectAlert={onSelectAlert} requestedSeverityFilter={requestedAlertFilter} onContactDriver={onContactDriver} onHoverAlert={onHoverAlert} resolvedAlertIds={resolvedAlertIds} dismissedAlertIds={dismissedAlertIds} onResolveAlert={onResolveAlert} onDismissAlert={onDismissAlert} data-id="element-468" />}
              {activeView === 'alerts' && alertTab === 'resolved' && (() => {
            const resolvedAlerts = alerts.filter(a => resolvedAlertIds.includes(a.id));
            if (resolvedAlerts.length === 0) {
              return <div className="flex-1 flex flex-col items-center justify-center p-8 text-center" data-id="element-469">
                        <CheckCircle2 className="w-8 h-8 text-brand-green/30 mb-3" data-id="element-470" />
                        <p className="text-sm text-slate-400 font-medium mb-1" data-id="element-471">
                          No resolved alerts
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-472">
                          Resolved alerts will appear here
                        </p>
                      </div>;
            }
            return <div className="flex-1 overflow-y-auto p-3 scrollbar-hide" data-id="element-473">
                      {resolvedAlerts.map(alert => {
                const driver = mockDrivers.find(d => d.id === alert.driverId);
                return <div key={alert.id} className="relative p-3 mb-2 last:mb-0 rounded-xl border bg-slate-900/30 border-slate-800 opacity-70" data-id="element-474">
                            <div className="flex items-start gap-3" data-id="element-475">
                              <div className="mt-0.5 text-brand-green" data-id="element-476">
                                <CheckCircle2 className="w-5 h-5" data-id="element-477" />
                              </div>
                              <div className="flex-1 min-w-0" data-id="element-478">
                                <div className="flex items-center justify-between mb-1" data-id="element-479">
                                  <h3 className="text-sm font-semibold truncate text-slate-400" data-id="element-480">
                                    {alert.title}
                                  </h3>
                                  <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border text-brand-green border-brand-green/30 bg-brand-green/10 shrink-0 ml-2" data-id="element-481">
                                    RESOLVED
                                  </span>
                                </div>
                                {driver && <div className="flex items-center gap-1.5 mb-1 text-slate-500" data-id="element-482">
                                    <TruckIcon className="w-3 h-3" data-id="element-483" />
                                    <span className="text-[10px] font-bold uppercase tracking-wider font-mono" data-id="element-484">
                                      {driver.truckId}
                                    </span>
                                  </div>}
                                <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed" data-id="element-485">
                                  {alert.description}
                                </p>
                              </div>
                            </div>
                          </div>;
              })}
                    </div>;
          })()}
              {activeView === 'alerts' && alertTab === 'dismissed' && (() => {
            const dismissedAlerts = alerts.filter(a => dismissedAlertIds.includes(a.id));
            if (dismissedAlerts.length === 0) {
              return <div className="flex-1 flex flex-col items-center justify-center p-8 text-center" data-id="element-486">
                        <XCircle className="w-8 h-8 text-slate-700 mb-3" data-id="element-487" />
                        <p className="text-sm text-slate-400 font-medium mb-1" data-id="element-488">
                          No dismissed alerts
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-489">
                          Dismissed alerts will appear here
                        </p>
                      </div>;
            }
            return <div className="flex-1 overflow-y-auto p-3 scrollbar-hide" data-id="element-490">
                      {dismissedAlerts.map(alert => {
                const driver = mockDrivers.find(d => d.id === alert.driverId);
                return <div key={alert.id} className="relative p-3 mb-2 last:mb-0 rounded-xl border bg-slate-900/30 border-slate-800 opacity-50" data-id="element-491">
                            <div className="flex items-start gap-3" data-id="element-492">
                              <div className="mt-0.5 text-slate-600" data-id="element-493">
                                <XCircle className="w-5 h-5" data-id="element-494" />
                              </div>
                              <div className="flex-1 min-w-0" data-id="element-495">
                                <div className="flex items-center justify-between mb-1" data-id="element-496">
                                  <h3 className="text-sm font-semibold truncate text-slate-500" data-id="element-497">
                                    {alert.title}
                                  </h3>
                                  <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border text-slate-500 border-slate-700 bg-slate-800 shrink-0 ml-2" data-id="element-498">
                                    DISMISSED
                                  </span>
                                </div>
                                {driver && <div className="flex items-center gap-1.5 mb-1 text-slate-600" data-id="element-499">
                                    <TruckIcon className="w-3 h-3" data-id="element-500" />
                                    <span className="text-[10px] font-bold uppercase tracking-wider font-mono" data-id="element-501">
                                      {driver.truckId}
                                    </span>
                                  </div>}
                                <p className="text-xs text-slate-600 line-clamp-2 leading-relaxed" data-id="element-502">
                                  {alert.description}
                                </p>
                              </div>
                            </div>
                          </div>;
              })}
                    </div>;
          })()}
              {activeView === 'fleet' && fleetTab === 'active' && <DriverList drivers={activeDrivers} selectedDriverId={selectedDriverId} onSelectDriver={onSelectDriver} variant="active" data-id="element-503" />}
              {activeView === 'fleet' && fleetTab === 'inactive' && (inactiveDrivers.length > 0 ? <DriverList drivers={inactiveDrivers} selectedDriverId={selectedDriverId} onSelectDriver={onSelectDriver} variant="inactive" data-id="element-504" /> : <div className="flex-1 flex flex-col items-center justify-center p-8 text-center" data-id="element-505">
                    <TruckIcon className="w-8 h-8 text-slate-700 mb-3" data-id="element-506" />
                    <p className="text-sm text-slate-400 font-medium mb-1" data-id="element-507">
                      No inactive drivers
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-508">
                      Drivers in sleeper berth will appear here
                    </p>
                  </div>)}
              {activeView === 'deliveries' && deliveryTab === 'enRoute' && <DeliveryList deliveries={enRouteDeliveries} selectedDriverId={selectedDriverId} onSelectDriver={onSelectDeliveryDriver} data-id="element-509" />}
              {activeView === 'deliveries' && deliveryTab === 'delivered' && (deliveredDeliveries.length > 0 ? <DeliveryList deliveries={deliveredDeliveries} selectedDriverId={selectedDriverId} onSelectDriver={onSelectDeliveryDriver} showFilters={false} data-id="element-510" /> : <div className="flex-1 flex flex-col items-center justify-center p-8 text-center" data-id="element-511">
                    <PackageIcon className="w-8 h-8 text-brand-green/30 mb-3" data-id="element-512" />
                    <p className="text-sm text-slate-400 font-medium mb-1" data-id="element-513">
                      No completed deliveries yet
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-514">
                      Completed deliveries will appear here
                    </p>
                  </div>)}
              {activeView === 'deliveries' && deliveryTab === 'undelivered' && (undeliveredDeliveries.length > 0 ? <DeliveryList deliveries={undeliveredDeliveries} selectedDriverId={selectedDriverId} onSelectDriver={onSelectDeliveryDriver} data-id="element-515" /> : <div className="flex-1 flex flex-col items-center justify-center p-8 text-center" data-id="element-516">
                    <PackageIcon className="w-8 h-8 text-brand-red/30 mb-3" data-id="element-517" />
                    <p className="text-sm text-slate-400 font-medium mb-1" data-id="element-518">
                      No undelivered items
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-519">
                      Failed or returned deliveries will appear here
                    </p>
                  </div>)}
              {activeView === 'triage' && <TriagePanel items={triageItems} selectedDriverId={selectedDriverId} onSelectDriver={onSelectDriver} onContactDriver={onContactDriver} data-id="element-520" />}
              {activeView === 'logs' && <LogsPanel entries={logEntries} data-id="element-521" />}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function RailButton({
  icon,
  label,
  isActive,
  onClick,
  hasUnread
}: {
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
  onClick: () => void;
  hasUnread?: boolean;
}) {
  return <button onClick={onClick} className={`relative flex flex-col items-center gap-1.5 w-14 py-2.5 rounded-xl transition-all
        ${isActive ? 'bg-slate-800 text-slate-100 shadow-sm' : 'text-slate-500 hover:text-slate-300 hover:bg-slate-900'}`} data-id="element-522">
      <div className="relative" data-id="element-523">
        {icon}
        {hasUnread && !isActive && <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-brand-red rounded-full border-2 border-slate-950" data-id="element-524" />}
      </div>
      <span className="text-[10px] font-medium leading-none" data-id="element-525">{label}</span>
    </button>;
}